import type { IsoDate } from '../../../../shared/types';
import { shell } from '../../app';
import { getSnapshot, onStoreChange } from '../../data/store';
import { relativeDay, timeAgo, todayLocal } from '../../logic/dates';
import { boardColumns } from '../../logic/desk';
import type { BoardCard } from '../../logic/desk';
import { searchLeads } from '../../logic/pipeline';
import { empty, heatDot, input, linkBtn, segmented } from '../../ui/components';
import { h, replaceChildren, span } from '../../ui/dom';
import { STAGE_LABELS } from '../../ui/labels';
import type { View } from '../../ui/router';
import { pipelineSections } from '../pipeline/sections';

type Mode = 'board' | 'list';
const MODE_KEY = 'sb.pipeline.mode';

const readMode = (): Mode => {
  try {
    return localStorage.getItem(MODE_KEY) === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
};

const boardCard = (c: BoardCard, today: IsoDate): HTMLElement =>
  h(
    'a',
    { class: 'bcard', href: `#/leads/${encodeURIComponent(c.lead.id)}` },
    h('div', { class: 'bcard__name' }, heatDot(c.lead.heat), span('bcard__title', c.lead.business_name)),
    h(
      'div',
      { class: 'bcard__meta' },
      [
        c.lead.neighborhood ?? c.lead.address,
        c.lastVisit ? `visited ${timeAgo(c.lastVisit)}` : 'never visited',
      ]
        .filter(Boolean)
        .join(' · '),
    ),
    c.next
      ? h(
          'div',
          {
            class: ['bcard__next', c.next.date < today ? 'bcard__next--late' : ''].filter(Boolean).join(' '),
          },
          `${c.next.action} · ${relativeDay(c.next.date, today)}`,
        )
      : null,
  );

/** The pipeline on the desktop (D65): a board of stage columns — or the grouped list — with search. */
export const pipelineDeskView: View = ({ root }) => {
  shell.setChrome(true);
  let mode = readMode();
  let showLost = false;
  let query = '';
  const area = h('div', { class: 'pipeline-desk' });
  const lostBtn = h(
    'button',
    {
      type: 'button',
      class: 'chip board-lost',
      'aria-pressed': 'false',
      onClick: () => {
        showLost = !showLost;
        lostBtn.setAttribute('aria-pressed', String(showLost));
        void render();
      },
    },
    'Show lost',
  );
  const search = input({
    type: 'search',
    placeholder: 'Search leads…',
    class: 'input--search',
    onInput: (v) => {
      query = v;
      void render();
    },
  });
  root.appendChild(
    h(
      'div',
      { class: 'page page--pipeline-desk' },
      h('div', { class: 'searchbar searchbar--desk' }, search, lostBtn),
      area,
    ),
  );
  shell.setTitle('Pipeline', {
    subtitle: 'Current stage · Won and Lost are closed',
    action: h(
      'div',
      { class: 'stack-h stack-h--tight' },
      segmented<Mode>({
        options: [
          { value: 'board', label: 'Board' },
          { value: 'list', label: 'List' },
        ],
        value: mode,
        onChange: (m) => {
          mode = m;
          try {
            localStorage.setItem(MODE_KEY, m);
          } catch {
            // private mode: the choice lasts this visit
          }
          void render();
        },
        size: 'md',
        columns: 2,
      }),
      linkBtn('Reports', '#/dashboard?from=pipeline', { variant: 'subtle', size: 'sm' }),
      linkBtn('+ New', '#/leads/new', { variant: 'subtle', size: 'sm' }),
    ),
  });

  // a sync (the window coming back into view) re-renders with the same leads: the board is kept as it
  // is, with each column's scroll, unless something on it changed
  let shownKey = '';
  const render = async () => {
    lostBtn.hidden = mode !== 'board';
    const snap = await getSnapshot();
    if (mode === 'list' || !snap.leads.length) shownKey = '';
    if (mode === 'list') return replaceChildren(area, pipelineSections(snap, query));
    if (!snap.leads.length)
      return replaceChildren(
        area,
        empty('No leads yet.', 'Use + New, or log a visit to create one on the spot.'),
      );
    const today = todayLocal();
    const columns = boardColumns(searchLeads(snap.leads, query), snap.visits, showLost);
    const key = JSON.stringify([today, columns.map((c) => [c.stage, c.cards.map((x) => [x.lead, x.lastVisit, x.next])])]);
    if (key === shownKey && area.querySelector('.board')) return;
    shownKey = key;
    replaceChildren(
      area,
      h(
        'div',
        { class: 'board' },
        columns.map((col) =>
          h(
            'section',
            { class: `board__col board__col--${col.stage}` },
            h(
              'header',
              { class: 'board__head' },
              span('board__title', STAGE_LABELS[col.stage]),
              span('board__count mono', String(col.cards.length)),
            ),
            h(
              'div',
              { class: 'board__cards' },
              col.cards.length
                ? col.cards.map((c) => boardCard(c, today))
                : h('p', { class: 'board__empty' }, query.trim() ? 'No match' : 'None'),
            ),
          ),
        ),
      ),
    );
  };

  void render();
  return onStoreChange(() => void render());
};
